import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/store";
import type {
  M365ConnectResponseDto,
  M365StatusDto,
} from "../dtos/M365ConnectDto";
import {
  initiateConnect,
  getStatus,
  disconnect,
} from "../infrastructure/m365ConnectorService";
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice";

function getErrorMessage(err: unknown, fallback: string) {
  if (typeof err === "object" && err !== null) {
    const maybeResponse = (err as { response?: unknown }).response;
    if (typeof maybeResponse === "object" && maybeResponse !== null) {
      const maybeData = (maybeResponse as { data?: unknown }).data;
      if (typeof maybeData === "object" && maybeData !== null) {
        const maybeMessage = (maybeData as { message?: unknown }).message;
        if (typeof maybeMessage === "string") return maybeMessage;
      }
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

interface M365ConnectorState {
  status: M365StatusDto | null;
  isLoading: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  error: string | null;
}

const initialState: M365ConnectorState = {
  status: null,
  isLoading: false,
  isConnecting: false,
  isDisconnecting: false,
  error: null,
};

export const startM365ConnectThunk = createAsyncThunk<
  M365ConnectResponseDto,
  void,
  { state: RootState; rejectValue: string }
>("profile/m365/startConnect", async (_, { getState, rejectWithValue }) => {
  try {
    const orgId = selectCurrentOrganizationId(getState());
    const spaReturnUrl = `${window.location.origin}/integrations?m365=connected`;
    const result = await initiateConnect(orgId, spaReturnUrl);

    // Backend may return either a plain URL string or a wrapped DTO { value: "url" }
    const redirectUrl = typeof result === "string" ? result : result?.value;

    if (!redirectUrl) {
      const dto = result as M365ConnectResponseDto;
      return rejectWithValue(dto?.error ?? "Failed to start M365 connection");
    }

    window.location.assign(redirectUrl);
    return typeof result === "string"
      ? ({ isSuccess: true, value: result } as M365ConnectResponseDto)
      : result;
  } catch (err: unknown) {
    return rejectWithValue(
      getErrorMessage(err, "Failed to start M365 connection"),
    );
  }
});

export const fetchM365StatusThunk = createAsyncThunk<
  M365StatusDto,
  void,
  { state: RootState; rejectValue: string }
>("profile/m365/fetchStatus", async (_, { getState, rejectWithValue }) => {
  try {
    const orgId = selectCurrentOrganizationId(getState());
    return await getStatus(orgId);
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to load M365 status"));
  }
});

export const disconnectM365Thunk = createAsyncThunk<
  void,
  void,
  { state: RootState; rejectValue: string }
>("profile/m365/disconnect", async (_, { getState, rejectWithValue }) => {
  try {
    const orgId = selectCurrentOrganizationId(getState());
    await disconnect(orgId);
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to disconnect M365"));
  }
});

const m365ConnectorSlice = createSlice({
  name: "profileM365Connector",
  initialState,
  reducers: {
    clearM365ConnectorError(state) {
      state.error = null;
    },
    clearM365ConnectorState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchM365StatusThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchM365StatusThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = action.payload;
      })
      .addCase(fetchM365StatusThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to load M365 status";
      })

      .addCase(startM365ConnectThunk.pending, (state) => {
        state.isConnecting = true;
        state.error = null;
      })
      .addCase(startM365ConnectThunk.fulfilled, (state) => {
        state.isConnecting = false;
      })
      .addCase(startM365ConnectThunk.rejected, (state, action) => {
        state.isConnecting = false;
        state.error = action.payload ?? "Failed to start M365 connection";
      })

      .addCase(disconnectM365Thunk.pending, (state) => {
        state.isDisconnecting = true;
        state.error = null;
      })
      .addCase(disconnectM365Thunk.fulfilled, (state) => {
        state.isDisconnecting = false;
        if (state.status) {
          state.status = {
            isConnected: false,
            upn: null,
            connectedAtUtc: null,
          };
        }
      })
      .addCase(disconnectM365Thunk.rejected, (state, action) => {
        state.isDisconnecting = false;
        state.error = action.payload ?? "Failed to disconnect M365";
      });
  },
});

export const { clearM365ConnectorError, clearM365ConnectorState } =
  m365ConnectorSlice.actions;

export default m365ConnectorSlice.reducer;

export const selectM365ConnectorState = (state: RootState) =>
  state.m365Connector;
export const selectM365Status = (state: RootState) =>
  state.m365Connector.status;
export const selectM365IsConnected = (state: RootState) =>
  state.m365Connector.status?.isConnected ?? false;
export const selectM365Loading = (state: RootState) =>
  state.m365Connector.isLoading;
export const selectM365Connecting = (state: RootState) =>
  state.m365Connector.isConnecting;
export const selectM365Disconnecting = (state: RootState) =>
  state.m365Connector.isDisconnecting;
export const selectM365Error = (state: RootState) => state.m365Connector.error;
